// Scrolls from the star rating near the product title down to the reviews. Prefers the
// reviews hero; falls back to the first reviews widget below the rating. The rating is
// third-party markup, so clicks are intercepted on this wrapper rather than by rewriting
// the widget's DOM.
class ProductRatingJump extends HTMLElement {
	static SELECTORS = {
		HERO: '.product-reviews-hero__section',
		// .product-reviews is the reviews section's own wrapper class, so it is server-rendered and
		// resolvable on connect. The provider widget class only exists once its script injects,
		// which is after this one runs — it is here for the click-time lookup, not the initial gate.
		// Templates that use Yotpo's app block instead of the reviews section have neither at connect;
		// the block does server-render a .yotpo-widget-instance div, matched via data-reviews-instance-id.
		FALLBACKS: '.product-reviews, .yotpo-reviews-main-widget',
		// Anything the injected widget exposes to the keyboard on its own. Negative tabindex is
		// excluded: it takes an element out of the tab order, so it is not an own control.
		OWN_CONTROL: 'a[href], button, [tabindex]:not([tabindex^="-"])',
		HINT: '[data-rating-jump-hint]',
		// Rendered by sections/product-reviews.liquid in drawer mode. Present at connect, so it
		// gates the affordance the same way a scroll target does.
		DRAWER: '#DrawerProductReviews',
		DRAWER_BODY: '#DrawerProductReviews .product-reviews__drawer-body',
		// Both header sections are position:sticky top:0, so the target has to clear whichever renders.
		STICKY_HEADER: '.header-v2, .header',
	};

	static CLASSES = {
		ENABLED: 'product__rating--jump-enabled',
	};

	static CAPTURE = { capture: true };

	static HINT_TEXT = {
		JUMP: ', jump to customer reviews',
		DRAWER: ', open customer reviews',
	};

	connectedCallback() {
		if (this.#drawer()) {
			// The drawer is server-rendered, but the widget inside it is not: drawer.js and this
			// script are deferred while the provider script is async, so the rating is otherwise
			// clickable during the placeholder shimmer and opens an empty panel that fills in later.
			// Hold the affordance until the panel has something to show.
			if (this.#drawerHasContent()) {
				this.#enable();
			} else {
				this.#watchDrawer();
			}
			return;
		}
		// No reviews on this template means no affordance at all, rather than a dead one.
		if (!this.#target()) {
			return;
		}
		this.#enable();
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.#onClick, ProductRatingJump.CAPTURE);
		this.removeEventListener('keydown', this.#onKeydown);
		this.widgetObserver?.disconnect();
		this.drawerObserver?.disconnect();
	}

	#enable() {
		if (this.#drawer()) {
			this.setAttribute('aria-haspopup', 'dialog');
		}
		this.classList.add(ProductRatingJump.CLASSES.ENABLED);
		// Capture phase: the provider's own star button scrolls its reviews widget into view from a
		// click listener of its own, which preventDefault cannot stop. Inside the drawer that scroll
		// runs on the panel and hides the header until focus lands on the close button. Taking the
		// click before it reaches the button makes this element the only handler.
		this.addEventListener('click', this.#onClick, ProductRatingJump.CAPTURE);
		this.#watchWidget();
	}

	#watchDrawer() {
		const body = document.querySelector(ProductRatingJump.SELECTORS.DRAWER_BODY);
		if (!body) {
			return;
		}
		this.drawerObserver = new MutationObserver(() => {
			if (!this.#drawerHasContent()) {
				return;
			}
			this.drawerObserver.disconnect();
			this.#enable();
		});
		this.drawerObserver.observe(body, { childList: true, subtree: true });
	}

	// Layout runs inside the closed drawer (it is translated off-screen, not display:none), so a
	// rendered widget has height and an un-rendered provider mount point has none.
	#drawerHasContent() {
		const body = document.querySelector(ProductRatingJump.SELECTORS.DRAWER_BODY);
		return !!body && body.offsetHeight > 0;
	}

	// The rating widget injects asynchronously, so the keyboard decision can't be made on
	// connect: at that point there is nothing rendered to measure and no control to detect.
	// Re-evaluate as the widget mutates.
	#watchWidget() {
		this.#syncKeyboardAffordance();
		this.widgetObserver = new MutationObserver(() => this.#syncKeyboardAffordance());
		this.widgetObserver.observe(this, { childList: true, subtree: true });
	}

	// Expose the wrapper to the keyboard only while it is worth focusing: the widget has
	// rendered something, and it exposes no control of its own (nesting a control inside a
	// role=button breaks WCAG 4.1.2, and focusing a zero-height box breaks 2.4.7).
	#syncKeyboardAffordance() {
		const hasOwnControl = !!this.querySelector(ProductRatingJump.SELECTORS.OWN_CONTROL);
		const isRendered = this.offsetHeight > 0;
		this.#setKeyboardAffordance(isRendered && !hasOwnControl);
	}

	#setKeyboardAffordance(enabled) {
		if (enabled === this.keyboardEnabled) {
			// The widget can re-render and wipe the subtree while the flag is unchanged, which
			// would leave role=button with no stated purpose.
			if (enabled) {
				this.#ensureHint();
			}
			return;
		}
		this.keyboardEnabled = enabled;

		if (!enabled) {
			this.removeAttribute('tabindex');
			this.removeAttribute('role');
			this.removeEventListener('keydown', this.#onKeydown);
			this.querySelector(ProductRatingJump.SELECTORS.HINT)?.remove();
			return;
		}

		this.tabIndex = 0;
		// role=button, not link: there is no href and nothing is navigated to, and button is the
		// role that takes both Enter and Space. No aria-label — it would replace the accessible
		// name and hide the rating itself, so the purpose is appended instead.
		this.setAttribute('role', 'button');
		this.#ensureHint();
		this.addEventListener('keydown', this.#onKeydown);
	}

	#ensureHint() {
		if (this.querySelector(ProductRatingJump.SELECTORS.HINT)) {
			return;
		}
		const hint = document.createElement('span');
		hint.className = 'sr-only';
		hint.dataset.ratingJumpHint = '';
		hint.textContent = this.#drawer() ? ProductRatingJump.HINT_TEXT.DRAWER : ProductRatingJump.HINT_TEXT.JUMP;
		this.append(hint);
	}

	#onClick = (event) => {
		const drawer = this.#drawer();
		if (drawer) {
			event.preventDefault();
			event.stopPropagation();
			// open() records event.currentTarget (this element) as the opener and calls focus() on it
			// when the drawer closes. While the widget exposes its own control this element carries no
			// tabindex, so that focus() would no-op and leave the keyboard on <body>. -1 makes it
			// focusable without adding a second tab stop next to the widget's control.
			if (!this.hasAttribute('tabindex')) {
				this.tabIndex = -1;
			}
			drawer.open(event);
			return;
		}
		const target = this.#target();
		if (!target) {
			return;
		}
		// The widget's own jump would land on the reviews list the hero keeps hidden while
		// collapse mode is on.
		event.preventDefault();
		event.stopPropagation();
		// block:'start' alone lands the target under the sticky header, hiding the eyebrow and the
		// top of the score — which reads as the same "nothing happened" this jump exists to fix.
		const headerHeight = document.querySelector(ProductRatingJump.SELECTORS.STICKY_HEADER)?.offsetHeight ?? 0;
		const top = target.getBoundingClientRect().top + window.scrollY - headerHeight;
		window.scrollTo({ top, behavior: 'smooth' });
	};

	#onKeydown = (event) => {
		if (event.key !== 'Enter' && event.key !== ' ') {
			return;
		}
		// Space would otherwise scroll the page.
		event.preventDefault();
		this.#onClick(event);
	};

	// Looked up per call, not cached: the theme editor re-renders the reviews section on its own.
	// Only an upgraded drawer-content counts; a not-yet-defined element has no open().
	#drawer() {
		const drawer = document.querySelector(ProductRatingJump.SELECTORS.DRAWER);
		return drawer && typeof drawer.open === 'function' ? drawer : null;
	}

	#fallbackSelector() {
		const instanceId = this.dataset.reviewsInstanceId;
		if (!instanceId) {
			return ProductRatingJump.SELECTORS.FALLBACKS;
		}
		return `${ProductRatingJump.SELECTORS.FALLBACKS}, .yotpo-widget-instance[data-yotpo-instance-id="${instanceId}"]`;
	}

	// Resolved per click, not cached: widgets inject asynchronously and the theme editor
	// re-renders sections independently of this one.
	#target() {
		const hero = document.querySelector(ProductRatingJump.SELECTORS.HERO);
		if (hero) {
			return hero;
		}
		// Only look below the rating. compareDocumentPosition reports descendants as FOLLOWING
		// too, so exclude our own subtree — the rating widget renders a provider container
		// inside this wrapper and would otherwise match itself.
		const candidates = document.querySelectorAll(this.#fallbackSelector());
		for (const candidate of candidates) {
			if (this.contains(candidate)) {
				continue;
			}
			if (this.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) {
				return candidate;
			}
		}
		return null;
	}
}

if (!customElements.get('product-rating-jump')) {
	customElements.define('product-rating-jump', ProductRatingJump);
}
